import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageContainer,
  PageHeader,
  PageHeaderActionSecondary,
  PageHeaderActions,
  PageHeaderLeading,
  PageHeaderStack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatRelativeTimeIso,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { BriefcaseBusiness, ExternalLink, History } from 'lucide-react'
import { toast } from 'sonner'

import {
  JOB_STEPS,
  PlatformAreaError,
  usePlatformAction,
  usePlatformJob,
  type JobStep,
  type PlatformActionRun,
  type PlatformJobDetail,
} from '../../hooks/use-platform-area'
import { ImproveReviewCanvas } from '../improve/improve-review-canvas'
import { PlatformJobRubric } from './platform-job-rubric'
import { ActionStatusBadge, PlatformOnlyNotice, SimulatedBadge, actionLogPath, formatScore } from './platform-area-parts'

const STEP_COPY: Record<JobStep, { title: string; description: string; button: string }> = {
  improve_job_description: {
    title: 'Improve the description',
    description: 'Draft a clearer description, review it side by side, then apply it.',
    button: 'Improve description',
  },
  reparse_job_skills: {
    title: 'Re-parse required skills',
    description: 'Read the required and preferred skills again from the job text and replace the current list.',
    button: 'Re-parse skills',
  },
  rescore_job_completeness: {
    title: 'Rescore completeness',
    description: 'Score how complete the job is and list what is missing.',
    button: 'Rescore',
  },
  rerun_job_analysis: {
    title: 'Re-run match analysis',
    description: 'Find matching candidates again and re-score applicants and prospects for this job.',
    button: 'Re-run analysis',
  },
}

const ADDED_VIA_LABEL: Record<string, string> = {
  application: 'Applicant',
  prospect: 'Prospect',
  search: 'Search',
  shortlist: 'Shortlist',
  manual: 'Added by hand',
}

function StepRunLine({ run }: { run: PlatformActionRun | null }) {
  if (!run) return <p className="text-sm text-muted-foreground">Not run yet.</p>
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <ActionStatusBadge status={run.status} />
      {run.summary ? <span>{run.summary}</span> : null}
      <span className="text-xs text-muted-foreground">
        {formatRelativeTimeIso(run.finished_at ?? run.started_at)}
        {run.actor_name || run.actor_email ? ` by ${run.actor_name || run.actor_email}` : ''}
      </span>
    </div>
  )
}

function StepCard({
  index,
  step,
  run,
  busy,
  onRun,
}: {
  index: number
  step: JobStep
  run: PlatformActionRun | null
  busy: boolean
  onRun: () => void
}) {
  const copy = STEP_COPY[step]
  const running = run?.status === 'running'
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <span
            aria-hidden
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium tabular-nums"
          >
            {index}
          </span>
          <h3>{copy.title}</h3>
        </CardTitle>
        <CardDescription>{copy.description}</CardDescription>
        <CardAction>
          <Button
            type="button"
            size="sm"
            variant={index === 1 ? 'default' : 'outline'}
            disabled={busy || running}
            onClick={onRun}
          >
            {running ? 'Running…' : copy.button}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <StepRunLine run={run} />
      </CardContent>
    </Card>
  )
}

function SkillList({ label, skills }: { label: string; skills: string[] }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        {label} <span className="font-normal text-muted-foreground tabular-nums">({skills.length})</span>
      </p>
      {skills.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {skills.map((skill) => (
            <Badge key={skill} variant="secondarySoft">
              {skill}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">None</p>
      )}
    </div>
  )
}

function JobSummary({ job }: { job: PlatformJobDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h3>Completeness and skills</h3>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-2xl font-semibold tabular-nums">{formatScore(job.completeness_score)}</span>
          {job.low_confidence ? <Badge variant="warningSoft">Low confidence</Badge> : null}
          {job.completeness_missing.length > 0 ? (
            <span className="text-sm text-muted-foreground">
              Missing {job.completeness_missing.join(', ').toLowerCase()}
            </span>
          ) : null}
        </div>
        <SkillList label="Required skills" skills={job.skills.required_skills} />
        <SkillList label="Preferred skills" skills={job.skills.preferred_skills} />
      </CardContent>
    </Card>
  )
}

function JobMatches({ job }: { job: PlatformJobDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h3>Candidate matches</h3>
        </CardTitle>
        <CardDescription>Top matches from the latest analysis.</CardDescription>
      </CardHeader>
      <CardContent>
        {job.matches.length === 0 ? (
          <p className="text-sm text-muted-foreground">No matches yet. Re-run the analysis to find candidates.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Match</TableHead>
                  <TableHead>Analysis</TableHead>
                  <TableHead>Evaluated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {job.matches.map((match) => (
                  <TableRow key={match.profile_id}>
                    <TableCell className="font-medium">{match.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ADDED_VIA_LABEL[match.added_via] ?? (match.added_via || 'Match')}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{match.match_percent}%</TableCell>
                    <TableCell>
                      <Badge variant={match.was_llm_judged ? 'successSoft' : 'secondarySoft'}>
                        {match.was_llm_judged ? 'Full analysis' : match.analysis_status || 'Quick match'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {match.evaluated_at ? formatRelativeTimeIso(match.evaluated_at) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function JobDescription({ description }: { description: string }) {
  const [open, setOpen] = useState(false)
  const long = description.length > 600
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h3>Current description</h3>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {description ? (
          <>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {open || !long ? description : `${description.slice(0, 600).trimEnd()}…`}
            </p>
            {long ? (
              <Button type="button" variant="link" size="sm" className="mt-2 px-0" onClick={() => setOpen(!open)}>
                {open ? 'Show less' : 'Show full description'}
              </Button>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No description.</p>
        )}
      </CardContent>
    </Card>
  )
}

export function PlatformJobPage() {
  const { jobId = '' } = useParams<{ jobId: string }>()
  const { data: job, isLoading, error } = usePlatformJob(jobId)
  const action = usePlatformAction()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const [confirmReparse, setConfirmReparse] = useState(false)

  useEffect(() => {
    document.title = job ? `${job.title} · Platform jobs` : 'Platform jobs'
  }, [job])

  if (error instanceof PlatformAreaError && (error.status === 401 || error.status === 403)) {
    return <PlatformOnlyNotice title="Jobs" />
  }

  const back = { label: 'Jobs', to: '/platform/jobs' }

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader leading={<PageHeaderLeading back={back} />} title="Job" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    )
  }

  if (!job) {
    return (
      <PageContainer>
        <PageHeader leading={<PageHeaderLeading back={back} />} title="Job" />
        <p className="text-sm text-muted-foreground">
          {error instanceof PlatformAreaError && error.status === 404
            ? 'This job no longer exists.'
            : (error as Error | null)?.message ?? 'This job could not be loaded.'}
        </p>
      </PageContainer>
    )
  }

  const run = (kind: 'rescore' | 'reparse-job' | 'rerun-analysis', started: string) =>
    action.mutate(
      { kind, jobId: job.id },
      {
        onSuccess: (result) => toast(result.status === 'running' ? started : result.summary || started),
        onError: (err) => toast.error(err.message),
      },
    )

  const runStep: Record<JobStep, () => void> = {
    improve_job_description: () =>
      openCanvas({
        title: `Improve job description: ${job.title}`,
        preventAutoFocus: true,
        content: (
          <ImproveReviewCanvas
            target={{ kind: 'opportunity', opportunityId: job.id, platform: true }}
            onClose={closeCanvas}
          />
        ),
      }),
    reparse_job_skills: () => setConfirmReparse(true),
    rescore_job_completeness: () => run('rescore', 'Completeness rescored.'),
    rerun_job_analysis: () => run('rerun-analysis', 'Re-running match analysis. Matches update when it finishes.'),
  }

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading back={back} />}
          title={job.title}
          description={job.organisation_name}
          icon={<BriefcaseBusiness className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              secondary={
                <>
                  {job.public_path ? (
                    <PageHeaderActionSecondary asChild>
                      <Link to={job.public_path}>
                        <ExternalLink className="h-4 w-4" aria-hidden />
                        View posting
                      </Link>
                    </PageHeaderActionSecondary>
                  ) : null}
                  <PageHeaderActionSecondary asChild>
                    <Link to={actionLogPath('job', job.id)}>
                      <History className="h-4 w-4" aria-hidden />
                      Action history
                    </Link>
                  </PageHeaderActionSecondary>
                </>
              }
            />
          }
        />
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={job.publication_status === 'published' ? 'successSoft' : 'secondarySoft'}>
            {job.publication_status.charAt(0).toUpperCase() + job.publication_status.slice(1)}
          </Badge>
          {job.is_simulation ? <SimulatedBadge /> : null}
        </div>
      </PageHeaderStack>

      <section aria-label="Platform steps" className="space-y-3">
        {JOB_STEPS.map((step, i) => (
          <StepCard
            key={step}
            index={i + 1}
            step={step}
            run={job.steps[step]}
            busy={action.isPending}
            onRun={runStep[step]}
          />
        ))}
      </section>

      <JobSummary job={job} />
      <PlatformJobRubric jobId={job.id} />
      <JobMatches job={job} />
      <JobDescription description={job.description} />

      <AlertDialog open={confirmReparse} onOpenChange={setConfirmReparse}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-parse skills?</AlertDialogTitle>
            <AlertDialogDescription>
              The required and preferred skills on “{job.title}” are read again from the job text and replace the
              current list. The title and description stay as they are. Matches for this job will refresh.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => run('reparse-job', 'Re-parsing skills. The page updates when it finishes.')}
            >
              Re-parse skills
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
